import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { type PrescriptionType, type PrescriptionMedication, medicalRecordsApi } from '@/api';
import { MeMedStatusBadge } from '@/components/memed/MeMedStatusBadge';
import { MemedPrescriptionButton } from '@/components/memed/MemedPrescriptionButton';
import { buildAddItemsPayload } from '@/hooks/memed/memed-item.mapper';
import type { MedikeiAllergy } from '@/hooks/memed/memed-allergy.mapper';
import { useMemedSync } from '@/hooks/useMeMed';
import { useMemedPrescription } from '@/hooks/useMemedPrescription';
import { Printer, ShieldCheck, Key, ExternalLink, Copy, Check, Lock } from 'lucide-react';

export interface PrescriptionItemProps {
  id: string;
  medications: PrescriptionMedication[];
  type: PrescriptionType;
  instructions?: string;
  isDigital: boolean;
  signedAt?: string;
  memedId?: string;
  memedPrescriptionId?: string;
  memedExcludedAt?: string | null;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  digitalPrescription?: {
    link?: string | null;
    unlockCode?: string | null;
  };
  unlockCode?: string | null;
  digitalPrescriptionLink?: string | null;
  pdfUrl?: string | null;
  memedPdfUrl?: string | null;
  content?: {
    medications?: any[];
    digitalPrescription?: {
      link?: string | null;
      unlockCode?: string | null;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

interface PrescriptionEditorProps {
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  patientName?: string;
  patientGender?: string;
  patientCpf?: string;
  patientBirthDate?: string;
  allergies?: MedikeiAllergy[];
  existingPrescriptions?: PrescriptionItemProps[];
  onPrescriptionAdded?: () => void;
  onPrescriptionPrinted?: (memedPrescriptionId: string) => void;
  disabled?: boolean;
}

const prescriptionTypeOptions: { value: PrescriptionType; label: string }[] = [
  { value: 'CONTINUOUS', label: 'Contínua' },
  { value: 'ACUTE', label: 'Aguda' },
  { value: 'CONTROLLED', label: 'Controlada' },
  { value: 'SPECIAL', label: 'Especial' },
];

export function PrescriptionEditor({
  medicalRecordId: _medicalRecordId,
  patientId,
  doctorId,
  patientName,
  patientGender,
  patientCpf,
  patientBirthDate,
  allergies = [],
  existingPrescriptions = [],
  onPrescriptionAdded,
  onPrescriptionPrinted,
  disabled = false,
}: PrescriptionEditorProps) {
  const safePatientName = patientName && patientName.trim().length > 0 ? patientName.trim() : 'Paciente';
  const safePatientGender = (() => {
    if (!patientGender) return 'MALE';
    const normalized = patientGender.trim().toUpperCase();
    if (normalized === 'FEMALE' || normalized === 'FEMININO' || normalized === 'F') {
      return 'FEMALE';
    }
    return 'MALE';
  })();

  const handlePrescriptionPrinted = useMemo(() => {
    if (onPrescriptionAdded && onPrescriptionPrinted) {
      return (id: string) => {
        onPrescriptionPrinted(id);
        onPrescriptionAdded();
      };
    }
    if (onPrescriptionPrinted) {
      return onPrescriptionPrinted;
    }
    if (onPrescriptionAdded) {
      return (_id: string) => {
        onPrescriptionAdded();
      };
    }
    return undefined;
  }, [onPrescriptionAdded, onPrescriptionPrinted]);

  const memedItems = useMemo(() => {
    const items = existingPrescriptions.flatMap((prescription) =>
      prescription.medications.map((medication) => ({
        nome: medication.nome || medication.name,
        presentation: medication.presentation || medication.descricao,
        concentration: medication.concentration,
        dose: medication.dose,
        frequency: medication.frequency,
        duration: medication.duration,
        quantidade:
          typeof medication.quantidade === 'number'
            ? medication.quantidade
            : typeof medication.quantidade === 'string'
            ? Number.parseInt(medication.quantidade, 10) || undefined
            : typeof medication.quantity === 'number'
            ? medication.quantity
            : undefined,
        observacoes: medication.observations,
      }))
    );
    return buildAddItemsPayload(items);
  }, [existingPrescriptions]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Prescrições Digitais (MeMed)</h3>
        <div className="flex items-center gap-2">
          {!disabled && (
            <MemedPrescriptionButton
              patient={{
                id: patientId,
                name: safePatientName,
                gender: safePatientGender,
                cpf: patientCpf,
                birthDate: patientBirthDate,
              }}
              allergies={allergies}
              professionalId={doctorId}
              items={memedItems}
              onPrescriptionPrinted={handlePrescriptionPrinted}
            />
          )}
        </div>
      </div>

      {existingPrescriptions.length > 0 && (
        <div className="space-y-4">
          {existingPrescriptions.map((prescription) => (
            <PrescriptionCard
              key={prescription.id}
              prescription={prescription}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {existingPrescriptions.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm font-medium text-gray-700">Nenhuma prescrição emitida.</p>
          <p className="text-xs mt-1 text-gray-500">
            Clique em "Prescrever via MeMed" acima para emitir uma receita digital com assinatura ICP-Brasil válida em farmácias de todo o Brasil.
          </p>
        </div>
      )}
    </div>
  );
}

function PrescriptionCard({
  prescription,
  disabled,
}: {
  prescription: PrescriptionItemProps;
  disabled?: boolean;
}) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [fetchedData, setFetchedData] = useState<{ link?: string; unlockCode?: string } | null>(null);
  const [isFetchingLink, setIsFetchingLink] = useState(false);

  const initialLink =
    prescription.digitalPrescription?.link ||
    prescription.content?.digitalPrescription?.link ||
    prescription.digitalPrescriptionLink ||
    prescription.memedPdfUrl ||
    prescription.pdfUrl ||
    null;

  const initialUnlockCode =
    prescription.digitalPrescription?.unlockCode ||
    prescription.content?.digitalPrescription?.unlockCode ||
    prescription.unlockCode ||
    null;

  const currentLink = fetchedData?.link || initialLink;
  const currentUnlockCode = fetchedData?.unlockCode || initialUnlockCode;
  const hasDigitalData = Boolean(currentLink || currentUnlockCode);

  const handleFetchDigitalLink = async () => {
    if (!prescription.id) return;
    setIsFetchingLink(true);
    try {
      const res = await medicalRecordsApi.getDigitalPrescriptionLink(prescription.id);
      if (res?.link || res?.unlockCode) {
        setFetchedData({ link: res.link, unlockCode: res.unlockCode });
      }
    } catch {
      // Falha silenciosa ou indisponível
    } finally {
      setIsFetchingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (!currentLink) return;
    navigator.clipboard?.writeText(currentLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyPin = () => {
    if (!currentUnlockCode) return;
    navigator.clipboard?.writeText(currentUnlockCode);
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-neutral-900/50 rounded-lg border border-gray-200 dark:border-neutral-800 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 rounded text-xs font-medium">
              {prescriptionTypeOptions.find(t => t.value === prescription.type)?.label || 'Aguda'}
            </span>
            {prescription.isDigital && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 rounded text-xs">
                Digital
              </span>
            )}
            {prescription.signedAt && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300 rounded text-xs">
                Assinado em {new Date(prescription.signedAt).toLocaleDateString('pt-BR')}
              </span>
            )}
            {prescription.memedStatus && (
              <MeMedStatusBadge
                status={prescription.memedStatus}
                memedId={prescription.memedId}
              />
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {prescription.medications.length} medicamento(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!prescription.memedId && !prescription.memedPrescriptionId && (
            <SendToMemedButton prescriptionId={prescription.id} />
          )}
          {prescription.memedStatus === 'FAILED' && (
            <RetryMemedButton prescriptionId={prescription.id} />
          )}
          {prescription.memedPrescriptionId && (
            <ReprintMemedButton
              memedPrescriptionId={prescription.memedPrescriptionId}
              disabled={disabled}
            />
          )}
        </div>
      </div>

      {/* Bloco de Receita Digital MeMed: Link & PIN de Desbloqueio */}
      {hasDigitalData ? (
        <div className="p-3 bg-blue-50/80 dark:bg-blue-950/40 rounded-lg border border-blue-200/80 dark:border-blue-800/60 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-blue-700 dark:text-blue-300 text-xs font-bold">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Receita Digital MeMed (ICP-Brasil)</span>
            </div>
            {currentUnlockCode && (
              <div className="flex items-center gap-1.5 bg-white dark:bg-card px-2.5 py-1 rounded-md border border-blue-200 dark:border-blue-800 text-xs shadow-2xs">
                <Lock className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-muted-foreground font-medium">PIN:</span>
                <span className="font-mono font-bold text-foreground text-sm tracking-wide">
                  {currentUnlockCode}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPin}
                  title="Copiar PIN"
                  className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                >
                  {copiedPin ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
          </div>

          {currentLink && (
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-blue-200/40 dark:border-blue-800/40">
              <span className="text-[11px] text-muted-foreground truncate max-w-xs sm:max-w-md font-mono">
                {currentLink}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-white dark:bg-card hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-blue-200 dark:border-blue-800 text-neutral-700 dark:text-neutral-200 cursor-pointer shadow-2xs"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedLink ? 'Copiado' : 'Copiar Link'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => window.open(currentLink, '_blank', 'noopener,noreferrer')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-blue-600 hover:bg-blue-700 text-white cursor-pointer shadow-2xs"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Abrir Receita</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : prescription.memedPrescriptionId ? (
        <div className="flex items-center justify-between p-2.5 bg-blue-50/40 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/40 text-xs">
          <span className="text-muted-foreground">
            Receita emitida via MeMed Sinapse.
          </span>
          <button
            type="button"
            disabled={isFetchingLink}
            onClick={handleFetchDigitalLink}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-white dark:bg-card text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded font-semibold hover:bg-blue-50 cursor-pointer text-xs"
          >
            <Key className="w-3.5 h-3.5" />
            <span>{isFetchingLink ? 'Carregando...' : 'Ver PIN e Link Digital'}</span>
          </button>
        </div>
      ) : null}

      {/* Lista rica de medicamentos */}
      <div className="space-y-2">
        {prescription.medications.map((med, idx) => {
          const medName = med.nome || med.name;
          const posology =
            med.sanitized_posology ||
            med.posologia ||
            (med.dose && med.frequency ? `${med.dose} - ${med.frequency}` : med.frequency || med.dose);
          const presentationInfo = med.presentation || med.descricao;
          const quantityText =
            med.quantidade !== undefined
              ? `${med.quantidade} ${med.unidade || 'unidades'}`
              : med.quantity !== undefined
              ? `${med.quantity} unidades`
              : null;

          return (
            <div
              key={idx}
              className="p-3 bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-neutral-800 space-y-1"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900 dark:text-foreground text-sm">
                    {medName}
                  </p>
                  {med.controle_especial && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800">
                      Controle Especial
                    </span>
                  )}
                  {med.receituario && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800">
                      {med.receituario}
                    </span>
                  )}
                </div>
                {med.fabricante && (
                  <span className="text-xs text-muted-foreground">
                    {med.fabricante}
                  </span>
                )}
              </div>

              {med.principio_ativo && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Princípio ativo:</span> {med.principio_ativo}
                </p>
              )}

              <p className="text-xs text-gray-600 dark:text-gray-300">
                {[presentationInfo, med.concentration, posology].filter(Boolean).join(' - ')}
              </p>

              {(quantityText || med.duration) && (
                <p className="text-xs text-gray-500">
                  {quantityText} {med.duration ? `- ${med.duration}` : ''}
                </p>
              )}

              {med.observations && (
                <p className="text-xs italic text-gray-500 bg-gray-50 dark:bg-neutral-900/60 p-1.5 rounded mt-1">
                  Obs: {med.observations}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {prescription.instructions && (
        <p className="text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-card p-2.5 rounded border border-gray-200 dark:border-neutral-800">
          <strong>Instruções:</strong> {prescription.instructions}
        </p>
      )}
    </div>
  );
}

function SendToMemedButton({ prescriptionId }: { prescriptionId: string }) {
  const { sendMutation } = useMemedSync(prescriptionId);

  if (sendMutation.isPending) {
    return (
      <Button size="sm" disabled>
        Enviando...
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => sendMutation.mutate()}
    >
      Enviar para MeMed
    </Button>
  );
}

function RetryMemedButton({ prescriptionId }: { prescriptionId: string }) {
  const { retryMutation } = useMemedSync(prescriptionId);

  if (retryMutation.isPending) {
    return (
      <Button size="sm" variant="outline" disabled>
        Tentando...
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => retryMutation.mutate()}
    >
      Tentar MeMed novamente
    </Button>
  );
}

function ReprintMemedButton({
  memedPrescriptionId,
  disabled,
}: {
  memedPrescriptionId: string;
  disabled?: boolean;
}) {
  const { viewMemedPrescription } = useMemedPrescription();

  return (
    <Button
      size="sm"
      variant="outline"
      aria-label="Reimprimir"
      onClick={() => viewMemedPrescription(memedPrescriptionId)}
      disabled={disabled}
      className="flex items-center gap-1.5"
    >
      <Printer className="h-3.5 w-3.5" />
      <span>Reimprimir (MeMed)</span>
    </Button>
  );
}
